// confirmRelief — the ONE place a staged relief becomes real.
//
// Staging is a row-only write: nobody has moved yet, so the room doc is still
// correct. CONFIRMING is different — the incoming person becomes the room's
// primary — and that fact lives in two documents.
//
// ⚠️ WHY THIS FILE EXISTS AT ALL
//
// `RoomCard.confirmTakeover` wrote only the assignment row. The card itself
// looks right afterwards, because it resolves `asg.ahpName || room.providerName`
// and the row now holds the new name — which is exactly why this survived: the
// screen that performs the action is the screen that cannot see the bug.
//
// `domain/channels.js:246-256` builds each room's chat membership from
// `room.providerId` / `room.providerName`. With a stale room doc, the room's
// channel keeps the RELIEVED CRNA and never gains the one who took over: the
// person who went home keeps receiving the room's messages, and the person
// standing in it does not. That module already carries a comment (`:358-360`)
// about the last time a stale room-doc provider caused precisely this.
//
// So the write is shared rather than duplicated. Two shells staging and
// confirming through two hand-written copies of a long patch is how they drift
// — the same reasoning `domain/relief.js` gives for existing at all.

import { takeoverPatch } from "../../domain/relief";
import { updateAssignmentRow, setRoomProvider } from "../../firebase/firestore";

/**
 * Confirm a staged relief: the staged person(s) become the room's primary.
 *
 * @param {object}  assignment  the day's assignment row (must carry .id)
 * @param {object=} opts
 * @param {string=} opts.roomId  the room doc to keep in step. Omitted only when
 *                               the caller genuinely has no room (tests).
 * @returns {Promise<object|null>} the patch written, or null when nothing was
 *                                 staged — callers treat null as a no-op so a
 *                                 double-tap cannot write an empty patch.
 */
export async function confirmRelief(assignment, { roomId } = {}) {
  if (!assignment || !assignment.id) return null;

  const patch = takeoverPatch(assignment);
  if (!patch) return null;                       // nothing staged; not an error

  await updateAssignmentRow(assignment.id, patch);

  if (!roomId) return patch;

  // Only the seats that were actually staged move. Confirming a CRNA-only
  // relief must not touch the supervising doctor on the room doc, or a
  // single-seat handoff would silently blank the other seat.
  const roomPatch = {};
  if (Object.prototype.hasOwnProperty.call(patch, "ahpId")) {
    roomPatch.providerId = patch.ahpId || null;
    roomPatch.providerName = patch.ahpName || "";
  }
  if (Object.prototype.hasOwnProperty.call(patch, "attendingId")) {
    roomPatch.supervisingDoctorId = patch.attendingId || null;
    roomPatch.supervisorName = patch.attendingName || "";
  }

  // setRoomProvider, never setRoomAssignment: the latter hardcodes
  // `status: "ready"` and would un-run a room mid-case.
  if (Object.keys(roomPatch).length) await setRoomProvider(roomId, roomPatch);

  return patch;
}

export default confirmRelief;
